import dayjs from "dayjs"
import config from "@/config"
import moment from "moment"
import { AxiosResponse } from "axios"
import store from "@/store"
import ajax from "@/utils/request"
/**
 * @param {Array} arr
 * @returns {Array}
 */
export function uniqueArr(arr: Array<any>): Array<any> {
    return Array.from(new Set(arr))
}

/**
 * @returns {string}
 */
export function createUniqueString(): string {
    const timestamp = +new Date() + ""
    const randomNum = Math.ceil((1 + Math.random()) * 65536) + ""
    return (+(randomNum + timestamp)).toString(32)
}

/**
 * Check if an element has a class
 * @param {HTMLElement} elm
 * @param {string} cls
 * @returns {boolean}
 */
export function hasClass(ele: HTMLElement, cls: string): boolean {
    return !!ele.className.match(new RegExp("(\\s|^)" + cls + "(\\s|$)"))
}

/**
 * Add class to element
 * @param {HTMLElement} elm
 * @param {string} cls
 */
export function addClass(ele: HTMLElement, cls: string) {
    if (!hasClass(ele, cls)) ele.className += " " + cls
}

/**
 * Remove class from element
 * @param {HTMLElement} elm
 * @param {string} cls
 */
export function removeClass(ele: HTMLElement, cls: string) {
    if (hasClass(ele, cls)) {
        const reg = new RegExp("(\\s|^)" + cls + "(\\s|$)")
        ele.className = ele.className.replace(reg, " ")
    }
}

/**
 * @param {string} name
 */
export function getUrlParam(name: string) {
    const reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)") // 构造一个含有目标参数的正则表达式对象
    const r = window.location.search.substr(1).match(reg) // 匹配目标参数
    if (r != null) return unescape(r[2])
    return null // 返回参数值
}

/**
 *  秒级时间戳转化为YYYY-MM-DD HH:mm:ss(unix时间戳)
 * @param {number} str
 */
export function sTimestampFormat(str: number) {
    if (str) {
        return dayjs.unix(str).format("YYYY-MM-DD HH:mm:ss")
    }
}

/**
 *  毫秒级时间戳转化为YYYY-MM-DD HH:mm:ss(unix时间戳)
 * @param {number} str
 */
export function msTimestampFormat(str: number, formatStr = "YYYY-MM-DD HH:mm:ss") {
    if (str) {
        return dayjs(str).format(formatStr)
    }
}

/**
 * 获取对应的cookie值
 * @param name
 * @returns cookie value
 */
export function getCookie(name: string) {
    const cookies = document.cookie
    const list = cookies.split("; ") // 解析出名/值对列表
    for (let i = 0; i < list.length; i++) {
        const arr = list[i].split("=") // 解析出名和值
        if (arr[0] == name) return arr[1] // 对cookie值解码
    }
    return ""
}
/**
 * redriectSSO
 */
export function redriectSSO() {
    const redirect = window.location.href
    const pid = config.api.PID
    const WXURL = config.api.SRED

    const ssoUrl = `${WXURL}?platform_id=${pid}&redirect=${escape(redirect)}`
    function sleep(s: number) {
        return new Promise((resolve: any) => {
            setTimeout(() => {
                resolve()
            }, s * 1000)
        })
    }
    // 企业微信内嵌应用免登录
    const ua = window.navigator.userAgent.toLowerCase()
    if (!!ua.match(/MicroMessenger/i) && !!ua.match(/wxwork/i)) {
        return ajax("SAPI2")
            .get("/auth/wechat/get-config")
            .then((data: any) => {
                const { app_id, state } = data
                const redirectUrl = encodeURIComponent(
                    `${WXURL}/login/wechat_callback?platform_id=${pid}&redirect=${escape(redirect)}`
                )
                window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${app_id}&redirect_uri=${redirectUrl}&response_type=code&scope=snsapi_base&state=${state}#wechat_redirect`
                return sleep(10)
            })
            .catch(() => {
                window.location.href = ssoUrl
                return sleep(10)
            })
    } else {
        const env = process.env.VUE_APP_ENV || "dev"
        const cookieName = env === "online" ? "st" : "st2"
        const st = getCookie(cookieName)
        if (st) {
            return ajax("SAPI2")
                .post("/auth/get-login-info", {
                    platform: pid,
                    st
                })
                .then((data: any) => {
                    const { token } = data
                    localStorage.setItem("token", token)
                    window.location.href = redirect
                    return sleep(10)
                })
                .catch(() => {
                    window.location.href = ssoUrl
                    return sleep(10)
                })
        } else {
            window.location.href = ssoUrl
            return sleep(10)
        }
    }
}

/**
 * 简易版深拷贝
 * @param {*} data 需要拷贝的数据
 */
export function deepClone(data: any) {
    return JSON.parse(JSON.stringify(data))
}

/**
 * 将数组转成map
 * @param {Array} arr 需要转换的数组
 * @param {*} key arr元素中需要转成key的属性值
 * @param {*} value  arr元素中需要转成value的属性值
 * 例如：arr2map([{ name: 'foo', age: 18 }], 'name', 'age') => {foo: 18}
 * 注意：如果有相同的key，则后续的对应value会覆盖掉前面的value
 */
export function arr2map(arr: any[], key = "key", value = "value", children = "children") {
    return arr.reduce((obj, item) => {
        if (item[children] && item[children].length) {
            const childrenMap = arr2map(item[children], key, value, children)
            Object.keys(childrenMap).forEach((k: string) => {
                obj[`${item[key]}_${k}`] = childrenMap[k] // 全路径
                obj[`${k}`] = childrenMap[k] // 子级
            })
        }
        obj[item[key]] = value ? item[value] : item // 如果value为空，则返回整个对象

        return obj
    }, {})
}

/**
 *  生成头像
 * @param {string} name
 */
export function createHead(name: string) {
    let str = ""
    for (let i = 0; i < name.length; i++) {
        str += name[i].charCodeAt(0).toString(16)
    }
    const color = "#" + str.slice(1, 4)
    return `<span style="padding: 6px; margin-right: 3px; color: white; font-weight: bolder; font-size: 14px; background-color: ${color}; border-radius: 40px;">${name.slice(
        0,
        1
    )}</span>`
}

/**
 * response post handler
 * @param response
 */
export function responseHandler(res: any) {
    if (res && typeof res.code !== "undefined" && res.code) {
        return Promise.reject(new Error(res.message || res.msg))
    }
    return Promise.resolve(res)
}

export function postHandlerTime(
    payload: any,
    cols: string[],
    options = { keyPos: "suffix", precision: "second" }
) {
    function format(val: any, precision: string, is_start: boolean) {
        let ret = moment(val).format("yyyy-MM-DD")
        if (precision == "second") {
            ret += is_start ? " 00:00:00" : " 23:59:59"
        }
        return ret
    }
    cols.forEach(col => {
        if (payload[col]) {
            const data = Array.isArray(payload[col]) ? payload[col] : [payload[col], payload[col]]
            if (data.length == 2) {
                const [start, end] = data
                let start_key = `${col}_start`
                let end_key = `${col}_end`
                if (options.keyPos == "prefix") {
                    start_key = `start_${col}`
                    end_key = `end_${col}`
                }
                payload[start_key] = format(start, options.precision, true)
                payload[end_key] = format(end, options.precision, false)
            }
            delete payload[col]
        }
    })
    return payload
}

export function postHandlerNull(payload: any) {
    return Object.entries(payload).reduce((pre: any, [k, v]) => {
        if (v !== null && typeof v !== "undefined" && JSON.stringify(v) != "[]") {
            pre[k] = v
        }
        return pre
    }, {})
}

export function postGroupType(
    groupType: any,
    property_group: any,
    property_group_type: any,
    isArray: boolean
) {
    if (!property_group) {
        return {}
    }
    const tree = groupType[property_group] || []
    const result: any = {
        property_group: isArray ? [property_group] : property_group
    }
    if (tree && tree.length) {
        tree.forEach((item: any) => {
            const { code, id, child } = item
            if (child && child.length) {
                child.forEach((items: any) => {
                    if (items.id === property_group_type) {
                        result.property_type = property_group_type
                        result.property_group_type = items.code
                        return
                    }
                })
            } else {
                if (id === property_group_type) {
                    result.property_group_type = code
                    return
                }
            }
        })
        return result
    } else {
        return result
    }
}

export function moment2Time(payload: any, cols: string[], defaultTime: any = 0) {
    cols.forEach(key => {
        if (payload[key]) {
            payload[key] = moment(payload[key]).format("yyyy-MM-DD")
        } else {
            payload[key] = defaultTime
        }
    })
    return payload
}

export function time2Moment(payload: any, cols: string[]) {
    cols.forEach(key => {
        if (payload[key]) {
            const val = payload[key]
            if (typeof val === "number") {
                payload[key] = moment.utc(val)
            } else {
                payload[key] = moment(val)
            }
        } else {
            payload[key] = null
        }
    })
    return payload
}

export function randomUid() {
    return Math.random()
        .toString(36)
        .substr(2)
}

export function formatTime(val: any) {
    if (!val) return "-"
    let time: moment.Moment
    if (moment.isMoment(val) || moment.isDate(val)) {
        time = moment(val)
    } else if (typeof val == "number") {
        time = moment.utc(val * 1000)
    } else {
        time = moment(val)
    }
    return time.format("yyyy-MM-DD")
}

export function exportFile(response: AxiosResponse) {
    const contentDisposition = response.headers["content-disposition"]
    let fileName = "unknown"
    if (contentDisposition) {
        const fileNameMatch =
            contentDisposition.match(/filename="(.+)"/) || contentDisposition.match(/filename=(.+)/)
        if (fileNameMatch.length === 2) fileName = fileNameMatch[1]
    }
    const blob = new Blob([response.data], {
        type: "application/octet-stream"
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    return
}

export function exportFileAcc(response: AxiosResponse) {
    const contentDisposition = response.headers["content-disposition"]
    let fileName = "unknown"
    if (contentDisposition) {
        const fileNameMatch =
            contentDisposition.match(/filename="(.+)"/) || contentDisposition.match(/filename=(.+)/)
        if (fileNameMatch.length === 2) fileName = fileNameMatch[1]
    }
    const blob = new Blob([response.data])
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    return
}

export function isMobile() {
    const ua = navigator.userAgent
    const ipad = ua.match(/(iPad).*OS\s([\d_]+)/),
        isIphone = ua.match(/(iPhone\sOS)\s([\d_]+)/),
        isAndroid = ua.match(/(Android)\s+([\d.]+)/),
        isMobile = ipad || isIphone || isAndroid
    return !!isMobile
}

export function isMac() {
    return /macintosh|mac os x/i.test(navigator.userAgent)
}

// 计算时间戳距离现在的天数
export function diffDay(text: number) {
    const now = moment()
    const dist = moment.utc(text * 1000)
    // 只比较日期，所以将比较时间的时分秒设置为00:00:00
    const distStart = dist.startOf("day")
    const nowStart = now.startOf("day")
    const diff = distStart.diff(nowStart, "day")
    const day = Math.abs(diff)
    const suffix = diff > 0 ? `${day}天后` : diff < 0 ? `${day}天前` : "今天"
    return `${dist.format("YYYY-MM-DD")} (${suffix})`
}

// 获取最近一个月时间范围
export function getLastMonth() {
    const end = dayjs(new Date()).format("YYYY-MM-DD 23:59:59")
    const start = new Date()
    start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
    return [dayjs(start).format("YYYY-MM-DD 00:00:00"), end]
}

// 获取时分秒
export function formatHour(time: any) {
    return dayjs(time).format("HH:mm:ss")
}

// 获取时分秒
export function formatDay(time: any) {
    return dayjs(time).format("YYYY-MM-DD")
}

// 获取时间戳
export function getTimestamp(str: any) {
    str = str.replace(/-/g, "/")
    if (!(str && new Date(str))) {
        return ""
    }
    return new Date(str).getTime()
}

// 将枚举值转成数组
export function getEnumValues(enumeration: { [key: string]: any }) {
    return Object.values(enumeration).filter(item => typeof item !== "number")
}

// 将枚举值转成[{ key, value }]形式；其中key，value字段可自定义
export function getEnumOptions(
    enumeration: { [key: string]: any },
    prop: { key: string; value: string } = { key: "key", value: "value" }
) {
    return getEnumValues(enumeration).map((val: any) => ({
        [prop.key]: enumeration[val],
        [prop.value]: val
    }))
}

export function detailPropertyGroupType(res: any) {
    const { property_group, property_group_type, property_type } = res
    const propertyGroupType = store.getters["common/property_group_type"]
    const tree = propertyGroupType[property_group] || []
    let result: any = ""
    if (tree.length) {
        if (property_type) {
            result = property_type
        } else {
            tree.forEach((item: any) => {
                const { code, id } = item
                if (code === property_group_type) {
                    result = id
                }
            })
        }
    }
    return result
}

export function titleCase(str: string) {
    return str.replace(/^\S/, s => s.toUpperCase())
}

export function openNewViewTab(url: string) {
    let link = document.createElement("a")
    link.rel = "noopener noreferrer"
    link.href = url
    link.target = "_blank"
    link.click()
    // 不和dom的根节点挂起来的话就是个普通的变量，随GC回收;时间具体是：内存占用达到很大之后触发的gc（垃圾回收）
    link = null
}
/**
 * @param {string} url
 * @returns {Boolean}
 */
export function validURL(url: string): boolean {
    const reg = /^(https?|ftp):\/\/([a-zA-Z0-9.-]+(:[a-zA-Z0-9.&%$-]+)*@)*((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.(com|edu|gov|int|mil|net|org|biz|arpa|info|name|pro|aero|coop|museum|[a-zA-Z]{2}|[a-zA-Z]{3,}))(:[0-9]+)*(\/($|[a-zA-Z0-9.,?'\\+&%$#=~_-]+))*$/
    return reg.test(url)
}

/**
 * @param {string} str
 * @param {string} title
 * @returns {Boolean}
 * 编辑器内容处理，获取Html
 * 并且将它拼接成一个完整的html页面
 */
export function str2Html(str: string, title: string) {
    const html =
        '<!DOCTYPE html> <html lang="en"> <head> <meta charset="UTF-8"> <meta http-equiv="X-UA-Compatible" content="IE=edge"><meta name="viewport" content="width=device-width, initial-scale=1.0"><meta name="color-scheme" content="dark light"><title>' +
        title +
        "</title></head>" +
        "<body>" +
        str +
        "</body></html>" +
        "<style>table { border-top: 1px solid #ccc; border-left: 1px solid #ccc; }" +
        "table td, th { border-bottom: 1px solid #ccc; border-right: 1px solid #ccc; padding: 3px 5px;}" +
        "table th { border-bottom: 2px solid #ccc; text-align: center; }" +
        "ul, ol { margin: 10px 0 10px 20px;} a{text-decoration: none; word-break: break-all;} a u{text-decoration: none; }" +
        ":root { --white_deep: #ffffff; --blue_link: #007aff; --font_gray_deep: #212121; }" +
        "html,body { background-color: var(--white_deep); color: var(--font_gray_deep);}" +
        "a { color: var(--blue_link)}" +
        "@media (prefers-color-scheme: dark) {:root {--white_deep: #1c1c1e;--blue_link: #0a84ff;--font_gray_deep: #f1f1f1;}}" +
        "</style>"
    return html
}
